import { useMemo } from "react";
import { useParams } from "react-router-dom";

import type { TreatmentOverview } from "./useTreatmentOverviews";
import { useTreatmentOverviews } from "./useTreatmentOverviews";

export const findTreatmentOverviewByCode = (
  treatments: TreatmentOverview[],
  treatmentCode: string
): TreatmentOverview | undefined => {
  const normalized = treatmentCode.toLowerCase();
  return treatments.find(
    (entry) => entry.treatmentCode?.toLowerCase() === normalized
  );
};

export type UseTreatmentOverviewByCodeResult = {
  treatment?: TreatmentOverview;
  hasData: boolean;
  loading: boolean;
  error?: Error;
  refetch: () => void;
};

/**
 * Loads a single treatment overview by route param, backed by the list query.
 * Supports deep links at /care/treatment/:treatmentCode (native + web).
 */
export const useTreatmentOverviewByCode =
  (): UseTreatmentOverviewByCodeResult => {
    const { treatmentCode: routeTreatmentCode } = useParams<{
      treatmentCode: string;
    }>();
    const treatmentCode = routeTreatmentCode
      ? decodeURIComponent(routeTreatmentCode)
      : undefined;

    const { treatments, hasData, loading, error, refetch } =
      useTreatmentOverviews();

    const treatment = useMemo(
      () =>
        treatmentCode
          ? findTreatmentOverviewByCode(treatments, treatmentCode)
          : undefined,
      [treatmentCode, treatments]
    );

    return {
      treatment,
      hasData,
      loading,
      error,
      refetch,
    };
  };
